import React, { useState } from "react";
import { CreditCard, Building, ArrowRight, X, Copy } from "lucide-react";
import { FlutterWaveButton, FlutterWaveButtonMain } from "../components/FlutterWaveButton";
import { PaystackButton, PaystackButtonMain } from "../components/PaystackButton";

const Donate = () => {
  const [amount, setAmount] = useState("");
  const [customAmount, setCustomAmount] = useState("");
  const [donationType, setDonationType] = useState("one-time");
  const [showModal, setShowModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("flutterwave");
  const [donorInfo, setDonorInfo] = useState({
    name: "",
    email: "",
    phone: "",
  });

  const presetAmounts = ["5000", "10000", "25000", "50000", "100000"];
  
  const getDonationAmount = () => {
    if (amount === "custom") {
      return parseInt(customAmount) || 0;
    }
    return parseInt(amount) || 0;
  };
  
  // Flutterwave configuration
  const flutterwaveConfig = {
    public_key: process.env.REACT_APP_FLUTTERWAVE_PUBLIC_KEY || "FLWPUBK-af2a36b1592b38aff9979243bc507bc5-X",
    tx_ref: `donation_${new Date().getTime()}_${Math.floor(Math.random() * 1000000)}`,
    amount: getDonationAmount(),
    currency: "NGN",
    payment_options: "card, banktransfer, ussd",
    customer: {
      email: donorInfo.email || "donor@example.com",
      phone_number: donorInfo.phone,
      name: donorInfo.name || "Anonymous Donor",
    },
    customizations: {
      title: "Oshoke Alasa Foundation",
      description: "Donation to support our cause",
      logo: "https://your-logo-url.com/logo.png",
    },
    meta: {
      donation_type: donationType,
    },
  };

  // Paystack configuration
  const paystackConfig = {
    public_key: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || "pk_live_5cd09733ba0a5b4af729341f2d402c9cbbde5434",
    meta: {
      donation_type: donationType,
    },
  };


  const onFlutterwaveSuccess = (response) => {
    console.log("Payment successful!", response);
    alert(`Thank you for your donation of ₦${getDonationAmount().toLocaleString()}! Your transaction reference is: ${response.transaction_id}`);
    // Reset form
    setAmount("");
    setCustomAmount("");
    setDonorInfo({ name: "", email: "", phone: "" });
  };

  const onFlutterwaveClose = () => {
    console.log("Payment closed");
  };

  const onPaystackSuccess = (response) => {
    console.log("Payment successful!", response);
    alert(`Thank you for your donation of ₦${getDonationAmount().toLocaleString()}! Your transaction reference is: ${response.reference}`);
    // Reset form
    setAmount("");
    setCustomAmount("");
    setDonorInfo({ name: "", email: "", phone: "" });
  };

  const onPaystackClose = () => {
    console.log("Payment closed");
  };


  const accountDetails = [
    { country: "Nigeria", currency: "NGN", flag: "🇳🇬", number: "1307301476" },
    { country: "United States", currency: "USD", flag: "🇺🇸", number: "1307629217" },
    { country: "United Kingdom", currency: "GBP", flag: "🇬🇧", number: "1307629224" },
    { country: "Europe", currency: "EUR", flag: "🇪🇺", number: "1307629231" },
  ];

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert("Account number copied to clipboard!");
  };

  return (
    <div className="relative">
      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-in fade-in zoom-in duration-300">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white z-10">
              <h3 className="font-serif text-2xl font-bold text-gray-900">Bank Transfer Details</h3>
              <button 
                onClick={() => setShowModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="bg-primary/5 rounded-xl p-4 border border-primary/10">
                <p className="text-sm text-gray-500 mb-1">Account Name</p>
                <p className="font-bold text-lg text-gray-900">Oshoke Alasa Foundation</p>
              </div>
              
              <div className="bg-primary/5 rounded-xl p-4 border border-primary/10">
                <p className="text-sm text-gray-500 mb-1">Bank Name</p>
                <p className="font-bold text-lg text-gray-900">Providus Bank</p>
              </div>

              <div className="space-y-3">
                <p className="font-medium text-gray-900">Select Currency Account:</p>
                {accountDetails.map((account, index) => (
                  <div key={index} className="flex items-center justify-between p-4 rounded-xl border border-gray-200 hover:border-primary/50 transition-colors bg-white">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">{account.flag} {account.country} ({account.currency})</p>
                      <p className="font-mono text-lg font-medium text-gray-900">{account.number}</p>
                    </div>
                    <button 
                      onClick={() => copyToClipboard(account.number)}
                      className="p-2 text-primary hover:bg-primary/5 rounded-lg transition-colors"
                      title="Copy account number"
                    >
                      <Copy className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>

              <div className="text-center text-sm text-gray-500 bg-gray-50 p-4 rounded-xl">
                <p>Please use your name as the transfer reference so we can acknowledge your donation.</p>
              </div>
            </div>
            
            <div className="p-6 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
              <button 
                onClick={() => setShowModal(false)}
                className="w-full bg-primary text-white py-3 rounded-xl font-medium hover:bg-primary-dark transition-colors"
              >
                I've Made the Transfer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="bg-cream py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-primary font-medium text-sm tracking-wider uppercase mb-3">SUPPORT OUR CAUSE</p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Make a Donation
            </h1>
            <p className="text-gray-600 text-lg">
              Your generosity helps us continue our mission of changing lives and building stronger communities across Nigeria.
            </p>
          </div>
        </div>
      </section>

      {/* Donation Form */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Form */}
            <div className="bg-cream rounded-2xl p-8 md:p-12">
              <h2 className="font-serif text-3xl font-bold text-gray-900 mb-8">Choose Your Donation</h2>

              {/* Donation Type */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-4">Donation Type</label>
                <div className="flex gap-4">
                  <button
                    onClick={() => setDonationType("one-time")}
                    className={`flex-1 py-3 px-6 rounded-full font-medium text-sm transition-colors ${
                      donationType === "one-time"
                        ? "bg-primary text-white"
                        : "bg-white text-gray-700 border border-gray-300"
                    }`}
                  >
                    One-Time
                  </button>
                  <button
                    onClick={() => setDonationType("monthly")}
                    className={`flex-1 py-3 px-6 rounded-full font-medium text-sm transition-colors ${
                      donationType === "monthly"
                        ? "bg-primary text-white"
                        : "bg-white text-gray-700 border border-gray-300"
                    }`}
                  >
                    Monthly
                  </button>
                </div>
              </div>

              {/* Amount Selection */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-4">Select Amount (₦)</label>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {presetAmounts.map((preset) => (
                    <button
                      key={preset}
                      onClick={() => {
                        setAmount(preset);
                        setCustomAmount("");
                      }}
                      className={`py-3 px-4 rounded-xl font-medium text-sm transition-colors ${
                        amount === preset
                          ? "bg-primary text-white"
                          : "bg-white text-gray-700 border border-gray-300 hover:border-primary"
                      }`}
                    >
                      ₦{parseInt(preset).toLocaleString()}
                    </button>
                  ))}
                  <button
                    onClick={() => setAmount("custom")}
                    className={`py-3 px-4 rounded-xl font-medium text-sm transition-colors ${
                      amount === "custom"
                        ? "bg-primary text-white"
                        : "bg-white text-gray-700 border border-gray-300 hover:border-primary"
                    }`}
                  >
                    Custom
                  </button>
                </div>
                {amount === "custom" && (
                  <input
                    type="number"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    placeholder="Enter custom amount"
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                )}
              </div>

              {/* Personal Info */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-4">Your Information</label>
                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder="Full Name"
                    value={donorInfo.name}
                    onChange={(e) => setDonorInfo({ ...donorInfo, name: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  <input
                    type="email"
                    placeholder="Email Address"
                    value={donorInfo.email}
                    onChange={(e) => setDonorInfo({ ...donorInfo, email: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  <input
                    type="tel"
                    placeholder="Phone Number"
                    value={donorInfo.phone}
                    onChange={(e) => setDonorInfo({ ...donorInfo, phone: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                </div>
              </div>

              {/* Payment Method Selection */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-4">Choose Payment Method</label>
                <div className="flex gap-4">
                  <button
                    onClick={() => setPaymentMethod("flutterwave")}
                    className={`flex-1 py-3 px-6 rounded-full font-medium text-sm transition-colors ${
                      paymentMethod === "flutterwave"
                        ? "bg-[#0085FF] text-white"
                        : "bg-white text-gray-700 border border-gray-300"
                    }`}
                  >
                    Flutterwave
                  </button>
                  <button
                    onClick={() => setPaymentMethod("paystack")}
                    className={`flex-1 py-3 px-6 rounded-full font-medium text-sm transition-colors ${
                      paymentMethod === "paystack"
                        ? "bg-[#00C3F7] text-white"
                        : "bg-white text-gray-700 border border-gray-300"
                    }`}
                  >
                    Paystack
                  </button>
                </div>
              </div>

              {/* Submit */}
              {paymentMethod === "flutterwave" ? (
                <FlutterWaveButtonMain 
                  config={flutterwaveConfig}
                  onSuccess={onFlutterwaveSuccess}
                  onClose={onFlutterwaveClose}
                  donorEmail={donorInfo.email}
                  donationAmount={getDonationAmount()}
                />
              ) : (
                <PaystackButtonMain 
                  config={paystackConfig}
                  onSuccess={onPaystackSuccess}
                  onClose={onPaystackClose}
                  donorEmail={donorInfo.email}
                  donationAmount={getDonationAmount()}
                />
              )}

              <p className="text-center text-gray-500 text-sm mt-4">
                Your donation is secure and encrypted
              </p>
            </div>

            {/* Impact Info - Removed */}
            <div>
              {/* Payment Options */}
              <div className="space-y-6">
                {/* Flutterwave Option */}
                <div className={`rounded-2xl p-8 text-white transition-all ${
                  paymentMethod === "flutterwave" 
                    ? "bg-gradient-to-br from-[#0085FF] to-[#005ACD]" 
                    : "bg-gray-200 text-gray-600"
                }`}>
                  <div className="flex items-center gap-3 mb-4">
                    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M2 4h20v2H2V4zm0 6h20v2H2v-2zm0 6h14v2H2v-2z"/>
                    </svg>
                    <h3 className="font-semibold text-xl">Flutterwave</h3>
                  </div>
                  <p className={`${paymentMethod === "flutterwave" ? "text-white/90" : "text-gray-500"} text-sm mb-6`}>
                    Pay with card, bank transfer, or USSD. Supports multiple currencies.
                  </p>
                  
                  {/* Amount display */}
                  {getDonationAmount() > 0 && paymentMethod === "flutterwave" && (
                    <div className="bg-white/20 rounded-lg p-3 mb-4 text-center">
                      <p className="text-sm text-white/80">You're donating</p>
                      <p className="text-2xl font-bold">₦{getDonationAmount().toLocaleString()}</p>
                    </div>
                  )}
                  
                  <FlutterWaveButton 
                    config={flutterwaveConfig}
                    onSuccess={onFlutterwaveSuccess}
                    onClose={onFlutterwaveClose}
                    donorEmail={donorInfo.email}
                    donationAmount={getDonationAmount()}
                  />
                  <p className={`text-center ${paymentMethod === "flutterwave" ? "text-white/70" : "text-gray-400"} text-xs mt-3`}>
                    Secured by Flutterwave
                  </p>
                </div>

                {/* Paystack Option */}
                <div className={`rounded-2xl p-8 text-white transition-all ${
                  paymentMethod === "paystack" 
                    ? "bg-gradient-to-br from-[#00C3F7] to-[#00A8CC]" 
                    : "bg-gray-200 text-gray-600"
                }`}>
                  <div className="flex items-center gap-3 mb-4">
                    <svg className="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M2 4h20v2H2V4zm0 6h20v2H2v-2zm0 6h14v2H2v-2z"/>
                    </svg>
                    <h3 className="font-semibold text-xl">Paystack</h3>
                  </div>
                  <p className={`${paymentMethod === "paystack" ? "text-white/90" : "text-gray-500"} text-sm mb-6`}>
                    Pay with card, bank transfer, or mobile money. Fast and secure.
                  </p>
                  
                  {/* Amount display */}
                  {getDonationAmount() > 0 && paymentMethod === "paystack" && (
                    <div className="bg-white/20 rounded-lg p-3 mb-4 text-center">
                      <p className="text-sm text-white/80">You're donating</p>
                      <p className="text-2xl font-bold">₦{getDonationAmount().toLocaleString()}</p>
                    </div>
                  )}
                  
                  <PaystackButton 
                    config={paystackConfig}
                    onSuccess={onPaystackSuccess}
                    onClose={onPaystackClose}
                    donorEmail={donorInfo.email}
                    donationAmount={getDonationAmount()}
                  />
                  <p className={`text-center ${paymentMethod === "paystack" ? "text-white/70" : "text-gray-400"} text-xs mt-3`}>
                    Secured by Paystack
                  </p>
                </div>
              </div>

              {/* Bank Account Details */}
              <div className="bg-gray-900 rounded-2xl p-8 text-white mt-8">
                <h3 className="font-semibold text-xl mb-6">Bank Account Details</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Building className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium mb-2">Account Name</p>
                      <p className="text-gray-300 text-sm">Oshoke Alasa Foundation</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <CreditCard className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium mb-2">Bank</p>
                      <p className="text-gray-300 text-sm">Providus Bank</p>
                    </div>
                  </div>
                  <div className="border-t border-white/10 pt-6">
                    <p className="font-medium mb-4">Account Numbers</p>
                    <div className="space-y-3">
                      <div className="bg-white/5 rounded-lg p-3">
                        <p className="text-xs text-gray-400 mb-1">🇳🇬 Nigeria (NGN)</p>
                        <p className="text-lg font-mono">1307301476</p>
                      </div>
                      <div className="bg-white/5 rounded-lg p-3">
                        <p className="text-xs text-gray-400 mb-1">🇺🇸 United States (USD)</p>
                        <p className="text-lg font-mono">1307629217</p>
                      </div>
                      <div className="bg-white/5 rounded-lg p-3">
                        <p className="text-xs text-gray-400 mb-1">🇬🇧 United Kingdom (GBP)</p>
                        <p className="text-lg font-mono">1307629224</p>
                      </div>
                      <div className="bg-white/5 rounded-lg p-3">
                        <p className="text-xs text-gray-400 mb-1">🇪🇺 Europe (EUR)</p>
                        <p className="text-lg font-mono">1307629231</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-20 bg-cream">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            100% of Your Donation Goes to Those in Need
          </h2>
          <p className="text-gray-600 text-lg mb-8">
            We are committed to transparency and accountability. Every naira you donate goes directly to supporting our programs and the communities we serve.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <p className="text-4xl font-bold text-primary mb-2">2500+</p>
              <p className="text-gray-600">Lives Impacted</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <p className="text-4xl font-bold text-primary mb-2">10+</p>
              <p className="text-gray-600">Campaigns Completed</p>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg">
              <p className="text-4xl font-bold text-primary mb-2">5+</p>
              <p className="text-gray-600">Communities Served</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-6">
            Have Questions About Donating?
          </h2>
          <p className="text-white/90 text-lg mb-8">
            We're here to help. Contact us for more information about how your donation will be used.
          </p>
          <a
            href="/contact"
            className="bg-white text-primary hover:bg-gray-100 px-8 py-4 rounded-full font-medium text-sm transition-colors inline-flex items-center gap-2"
          >
            CONTACT US <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>
    </div>
  );
};

export default Donate;
